import React from 'react';
import { View, Image, Text, ViewStyle } from 'react-native';

export interface AvatarProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | number;
  source?: { uri: string } | number | undefined;
  name?: string | undefined;
  fallbackText?: string;
  backgroundColor?: string;
  textColor?: string;
  style?: ViewStyle;
  testID?: string;
}

export function Avatar({
  size = 'md',
  source,
  name,
  fallbackText,
  backgroundColor = '#ea580c',
  textColor = '#ffffff',
  style,
  testID,
}: AvatarProps) {
  const [imageError, setImageError] = React.useState(false);

  // Material Design 500 colors for avatar backgrounds (Gmail style)
  const AVATAR_COLORS = [
    '#ef4444', // red-500
    '#f97316', // orange-500
    '#f59e0b', // amber-500
    '#84cc16', // lime-500
    '#10b981', // emerald-500
    '#06b6d4', // cyan-500
    '#3b82f6', // blue-500
    '#6366f1', // indigo-500
    '#8b5cf6', // violet-500
    '#d946ef', // fuchsia-500
    '#f43f5e', // rose-500
  ];

  const getAvatarColor = (identifier: string): string => {
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
    }
    return (
      AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] || AVATAR_COLORS[0]
    );
  };

  const sizes = { xs: 24, sm: 32, md: 40, lg: 48, xl: 64, '2xl': 80 };
  const avatarSize = typeof size === 'number' ? size : sizes[size];
  const fontSize = Math.floor(avatarSize * 0.4);

  const getInitials = (name?: string): string => {
    if (!name) return fallbackText || '?';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0]?.slice(0, 2).toUpperCase() || fallbackText || '?';
    }
    const first = words[0]?.charAt(0) || '';
    const last = words[words.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase();
  };

  // Use dynamic color if no background provided and we have a name
  const finalBackgroundColor =
    backgroundColor === '#ea580c' && name
      ? getAvatarColor(name)
      : backgroundColor;

  return (
    <View
      style={[
        {
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarSize / 2,
          backgroundColor:
            source && !imageError ? 'transparent' : finalBackgroundColor,
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        },
        style,
      ]}
      testID={testID}
    >
      {source && !imageError ? (
        <Image
          source={source}
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: avatarSize / 2,
          }}
          resizeMode='cover'
          onError={() => setImageError(true)}
        />
      ) : (
        <Text
          style={{
            fontSize,
            fontWeight: '600',
            color: textColor,
            textAlign: 'center',
          }}
        >
          {getInitials(name)}
        </Text>
      )}
    </View>
  );
}

export interface AvatarGroupProps {
  avatars: Array<{
    source?: { uri: string } | number | undefined;
    name?: string | undefined;
  }>;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  max?: number;
  spacing?: number;
  testID?: string;
}

export function AvatarGroup({
  avatars,
  size = 'md',
  max = 3,
  spacing = -8,
  testID,
}: AvatarGroupProps) {
  const sizes = { xs: 24, sm: 32, md: 40, lg: 48, xl: 64, '2xl': 80 };
  const avatarSize = sizes[size];
  const displayAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        height: avatarSize,
      }}
      testID={testID}
    >
      {displayAvatars.map((avatar, index) => (
        <View
          key={index}
          style={{
            marginLeft: index > 0 ? spacing : 0,
            zIndex: displayAvatars.length - index,
            borderWidth: 2,
            borderColor: '#ffffff',
            borderRadius: avatarSize / 2,
          }}
        >
          <Avatar size={size} source={avatar.source} name={avatar.name} />
        </View>
      ))}

      {remainingCount > 0 && (
        <View
          style={{
            marginLeft: spacing,
            zIndex: 0,
            borderWidth: 2,
            borderColor: '#ffffff',
            borderRadius: avatarSize / 2,
          }}
        >
          <Avatar
            size={size}
            name={`+${remainingCount}`}
            backgroundColor='#9ca3af'
          />
        </View>
      )}
    </View>
  );
}
